import type { Article } from "./types";

export const article: Article = {
  slug: "bible-in-a-year-on-your-phone",
  title: "Reading the Bible in a year on the device that distracts you",
  h1: "Reading the Bible in a year, on your phone",
  description:
    "Most yearly plans die in Leviticus, in February. Here is why the phone makes that both likelier and fixable, and how to pick a plan you will finish.",
  targets: [
    "read the bible in a year",
    "bible reading plan app",
    "bible in a year plan that works",
    "keep up with bible reading plan",
  ],
  published: "2026-08-17",
  standfirst:
    "Nearly every abandoned plan dies in the same place, for the same two reasons, and neither of them is Leviticus.",
  takeaways: [
    "Yearly plans fail from the debt, not the difficulty: miss four days and the plan starts telling you that you are behind, every day, until you stop opening it.",
    "Choose a plan you can fall behind on without it breaking — a book-order or a two-track plan absorbs a missed week that a strict calendar cannot.",
    "Read on the phone if that is what you have, but close what else is on it during the reading, or the reading loses to the same device it is on.",
    "Twenty minutes at a fixed anchor beats an hour when you are inspired; the plan is finished by the ordinary days, not the good ones.",
  ],
  body: [
    {
      type: "p",
      text: "The statistic everybody quotes about yearly Bible reading plans is that most people stop in February, somewhere in Leviticus. The book gets the blame and it does not deserve it. The plans fail in February because that is roughly when the first missed week has compounded into a number the reader cannot face — and it is the number, not the ceremonial law, that ends it.",
    },
    {
      type: "h2",
      id: "debt",
      text: "Plans fail from debt, not from difficulty",
    },
    {
      type: "p",
      text: "A dated plan has a property nobody notices when they start: it converts a missed day into a debt, and the debt is visible every time you open it. Miss four days over a busy fortnight and the plan now greets you with a backlog before it greets you with scripture. Nobody keeps opening something that opens with an accusation.",
    },
    {
      type: "p",
      text: "That is why the same reader can fail at a January-to-December plan three years running and read the entire New Testament in a summer without noticing. The second one had nothing to fall behind on.",
    },
    {
      type: "note",
      text: "Pick a plan that can absorb a missed week. That single property predicts finishing better than any other.",
    },
    {
      type: "h2",
      id: "plans",
      text: "Which plan to actually pick",
    },
    {
      type: "p",
      text: "There are hundreds and the differences that matter are few. In rough order of how forgiving they are:",
    },
    {
      type: "ul",
      items: [
        "Book order, no dates. Read straight through at whatever pace. Nothing to fall behind on, and the whole shape of a book stays intact. The weakest option for accountability and the strongest for finishing.",
        "Two-track (one Old Testament passage, one New). The variety carries you through the harder stretches, and a missed day costs two short readings rather than a chapter of genealogy.",
        "Chronological. Genuinely illuminating — the prophets land differently when they sit inside the history. Also the least forgiving, because the order only makes sense if you keep it.",
        "Ninety-day sprints. A quarter of the Bible at speed, then a break. Best for people who have failed at yearly plans, because the finish line is close enough to see.",
      ],
    },
    {
      type: "p",
      text: "If you have abandoned a yearly plan twice, do not attempt a third one. Read one book, well, and finish it. Completion is the skill being built here, and it transfers.",
    },
    {
      type: "h2",
      id: "phone",
      text: "The phone is the right tool and the wrong room",
    },
    {
      type: "p",
      text: "There is a persistent idea that reading scripture on a phone is a lesser act. It is not — a Bible on a phone is still a Bible, it is searchable, it is with you in the twenty minutes you actually have, and it is the version most people will genuinely read.",
    },
    {
      type: "p",
      text: "The real problem is narrower and worth naming precisely: the device holding the passage also holds everything designed to be more immediately rewarding than the passage. Ten minutes into Jeremiah a notification arrives, and you do not return. This is not a failure of reverence, it is a failure of environment.",
    },
    {
      type: "p",
      text: "So: read on the phone, and close the rest of it while you do. A reading window with the feed apps blocked is the same twenty minutes with the interruptions removed — which is the entire difference between a plan that survives and one that does not. It is the same mechanism, pointed at a different hour, as {why-a-block-with-a-cost-outlasts-a-limit}.",
      links: {
        "why-a-block-with-a-cost-outlasts-a-limit":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "h2",
      id: "how",
      text: "How to read so that it holds",
    },
    {
      type: "ol",
      items: [
        "Anchor the reading to an event, not a time — the commute, after the kettle, the twenty minutes before bed. A time you have to remember is a time you will eventually not.",
        "Read the introduction to each book. Four minutes that make forty chapters legible; skipping them is most of why Leviticus feels like a wall.",
        "Keep one note per book, not per chapter. Anything more granular becomes a second obligation and dies before the reading does.",
        "When you fall behind, skip forward to today rather than catching up. The point is the habit and the whole text over a life, not a compliance record for one calendar year.",
      ],
    },
    {
      type: "verse",
      verse: {
        text: "Your word is a lamp to my feet, and a light for my path.",
        ref: "Psalm 119:105",
      },
    },
    {
      type: "p",
      text: "Psalm 119 is the longest chapter in the Bible and is entirely about loving a text. It is also, read at one sitting, quite repetitive — which is a useful thing to notice early. Scripture is not uniformly gripping, and a plan built on the expectation that it will be is a plan built to break.",
    },
    {
      type: "h2",
      id: "app",
      text: "What to look for in an app",
    },
    {
      type: "p",
      text: "Most Bible apps do the same things. Three properties actually separate them for this purpose:",
    },
    {
      type: "ul",
      items: [
        "It remembers where you were, exactly, and returns you there without a menu. Every tap between opening and reading is a place to lose someone.",
        "Multiple translations, side by side or one tap apart. A verse that reads as nonsense in one rendering is often clear in another.",
        "It does not need you to open it to reach you. A verse that arrives on the home screen is read by people who would not have opened anything.",
      ],
    },
    {
      type: "p",
      text: "{Bible-Shield} has the whole Bible built for reading — several translations, search across every verse, highlights, notes and cross-references, with your place kept and progress tracked — and it is the same app that closes the feed apps during your reading window. That combination is the point rather than a coincidence: the reading and the thing that interrupts it are on one device, so the tool that helps has to sit on both sides of it.",
      links: { "Bible-Shield": "/" },
    },
    {
      type: "p",
      text: "A last, cheerful note on what a year of reading is actually for. It is not recall — but recall is a decent proxy for whether the reading is landing, and {fifteen-questions-most-people-get-wrong} is a quick way to find out how much of what you are sure of came from the page rather than from a carol.",
      links: {
        "fifteen-questions-most-people-get-wrong": "/quiz/bible-knowledge-quiz",
      },
    },
  ],
  faq: [
    {
      q: "What is the best Bible reading plan for beginners?",
      a: "A two-track plan, or simply the Gospel of Mark followed by Genesis. Avoid a dated yearly plan for a first attempt — the debt it creates after one missed week is what ends most beginners' reading, not the difficulty of the text.",
    },
    {
      q: "How long does it take to read the Bible in a year?",
      a: "Around twelve to fifteen minutes a day at ordinary reading speed, which is less than most people expect. The constraint is almost never the minutes; it is the uninterrupted minutes.",
    },
    {
      q: "Is it okay to read the Bible on a phone?",
      a: "Yes. It is searchable, it is with you, and it is the version most people will actually read. The only real caveat is that the device also carries everything competing for the same twenty minutes — which is an argument for blocking those during the window, not for buying a print edition you will not carry.",
    },
    {
      q: "What if I fall behind?",
      a: "Skip to today rather than catching up. Catching up turns reading into arrears, and arrears are what people stop opening.",
    },
  ],
  related: ["morning-quiet-time-routine", "phone-addiction-bible-verses"],
};
